import moment from "moment";
import React, { useCallback, useMemo, useState } from "react";
import styled, { withTheme } from "styled-components";
import { currentStudy } from "../../../../config";
import { DateFormat } from "../../../../modules/constants";
import { exportCSVFile } from "../../../../modules/utils";
import AnimatedButton from "../../../ui-library/Buttons/AnimatedButton/AnimatedButton";
import CustomDropdown from "../../../ui-library/CustomDropdown/CustomDropdown";
import DateSelect from "../../../ui-library/DateAndTime/Date/DateSelect";
import { reportTypes } from "../../../../modules/constants";
import {
  getDataTo24HRecallReport,
  headers24hRecall,
} from "../../reports/CSV/24hRecall";
import {
  getDataToDailyAveragesReport,
  headersDailyAverages,
} from "../../reports/CSV/DailyAverages";

const Label = styled.span`
  color: ${(props) => props.theme?.mortar};
  font-size: 15px;
  font-weight: 600;
`;

const DatePickerWrapper = styled.div`
  div.betweenDays.react-datepicker__day--disabled
    ~ div:not(.betweenDays):not(.react-datepicker__day--disabled):not(:hover) {
    background-color: ${(props) => props?.theme?.lightPurple};
    border-radius: 0.3rem;
  }

  div.betweenDays.react-datepicker__day--disabled:not(.react-datepicker__day--selected) {
    background-color: ${(props) => props?.theme?.lightPurple};
    border-radius: 0.3rem;
  }

  div[class~="react-datepicker__day--in-selecting-range"]:not(.react-datepicker__day--selected) {
    background-color: ${(props) => props?.theme?.lightPurple};
    border-radius: 0.3rem;
  }

  .react-datepicker__day--range-start {
    background-color: ${(props) => props?.theme?.darkPurple};
  }

  div.react-datepicker__day--in-selecting-range:hover {
    background-color: ${(props) => props?.theme?.darkPurple};
  }

  .react-datepicker-popper {
    position: relative !important;
    transform: none !important;
    width: 240px;
    height: 490px;
  }

  @media (max-width: 320px) {
    .react-datepicker__input-container div {
      padding-left: 1.8rem;
      padding-right: 1.8rem;
    }

    .react-datepicker__input-container div i:first-child {
      left: 5px;
    }
  }
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-top: 38px;
  @media (max-width: 576px) {
    padding-top: 2rem;
    margin-top: auto;
    .btn {
      width: 100%;
    }
  }
`;

const StyledError = styled.div`
  font-weight: 600;
  font-size: 12px;
  color: ${(props) => props.theme?.cardinal};
  text-transform: uppercase;
`;

const ReportGeneratorOptions = (props) => {
  const { fetchEntriesData, participant } = props;
  const [selectedReportType, setSelectedReportType] = useState(reportTypes.CSV);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showErrorDate, setShowErrorDate] = useState(false);
  const [isValidDate, setIsValidDate] = useState(true);
  const [errors, setErrors] = useState({});
  const [isGenerateReportSuccess, setGenerateReportSuccess] = useState(false);
  const [isGenerateReportError, setGenerateReportError] = useState(false);

  const btnGenerateReportErrorMngmt = {
    isSuccess: isGenerateReportSuccess,
    setIsSuccess: setGenerateReportSuccess,
    isError: isGenerateReportError,
    setIsError: setGenerateReportError,
  };

  const isDateRangeValid = useMemo(() => {
    return startDate !== "" && endDate !== "";
  }, [startDate, endDate]);

  const isReportTypeValid = useMemo(() => {
    return !!selectedReportType;
  }, [selectedReportType]);

  const generateNutricionReport = () => {
    validateReportType();
    validateDateRange();

    if (isDateRangeValid) {
      switch (selectedReportType?.id) {
        case reportTypes.CSV_24H_RECALL.id:
          getCSVReport("24H_", headers24hRecall, (reportData) =>
            getDataTo24HRecallReport(reportData)
          );
          break;
        case reportTypes.CSV_DAILY_AVERAGES.id:
          getCSVReport(
            "Daily_",
            headersDailyAverages,
            (reportData, start, end) =>
              getDataToDailyAveragesReport(reportData, start, end)
          );
          break;

        default:
          break;
      }
    } else {
      setGenerateReportError(true);
    }
  };

  const getCSVReport = (fileNamePrefix, headers, getDataToReport) => {
    const start = moment(startDate)
      .startOf("day")
      .format(DateFormat.START_DATE);
    const end = moment(endDate).endOf("day").format(DateFormat.END_DATE);

    fetchEntriesData(start, end, participant.participantId).then(
      (reportData) => {
        const fileName = `${fileNamePrefix}${participant.userName}-${reportData.generatedDate}`;
        exportCSVFile(
          headers,
          getDataToReport(reportData, start, end),
          reportData.generatedDate,
          participant.userName,
          currentStudy(),
          fileName
        )
          .then(() => {
            setGenerateReportSuccess(true);
          })
          .catch(() => {
            setGenerateReportError(true);
          });
      }
    );
  };

  const handleReportTypeOnChange = (value) => {
    if (value) {
      setSelectedReportType(value);
    }

    setStartDate("");
    setEndDate("");
    setErrors(null);
    setGenerateReportError(true);
  };

  const errorHandling = {
    showErrorDate: showErrorDate,
    setShowErrorDate: setShowErrorDate,
    isValidDate: isValidDate,
    setIsValidDate: setIsValidDate,
  };

  const onDateChange = (dates) => {
    const [start, end] = dates;

    if (start && end) {
      setStartDate(start);
      setEndDate(end);
    }
  };

  //Reset calendar state
  const onCalendarClose = () => {
    if (startDate && !endDate) {
      onDateChange([startDate, startDate]);
    }
  };

  const validateReportType = useCallback(
    (value) => {
      if (!isReportTypeValid) {
        setGenerateReportError(true);
      }
      setErrors((prevState) => ({
        ...prevState,
        reportType: isReportTypeValid
          ? undefined
          : "Please select a report type.",
      }));
    },
    [isReportTypeValid]
  );

  const getErrorMsg = useMemo(() => {
    return "Please select a date range";
  }, []);

  const validateDateRange = useCallback(
    (value) => {
      setErrors((prevState) => ({
        ...prevState,
        dateRange: isDateRangeValid ? undefined : getErrorMsg,
      }));
    },
    [getErrorMsg, isDateRangeValid]
  );

  return (
    <div data-testid="nutrition-report">
      <div className="mb-4">
        <Label className="mb-1">Select report type:</Label>
        <CustomDropdown
          placeholder="Select"
          values={[reportTypes.CSV_DAILY_AVERAGES, reportTypes.CSV_24H_RECALL]}
          onChange={(e) => handleReportTypeOnChange(e)}
          width={200}
        />
        <StyledError className="mt-2">
          {errors?.reportType && <span>{errors?.reportType}</span>}
        </StyledError>
      </div>

      {selectedReportType && (
        <div>
          <Label htmlFor="dateselect" className="mb-1">
            Date range:
          </Label>
          <DatePickerWrapper>
            <DateSelect
              data-testid="sted"
              eventDate={startDate}
              setEventDate={setStartDate}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              errorHandling={errorHandling}
              monthsShown={2}
              selectsRange={true}
              displayTitle={false}
              maxDate={new Date()}
              onCalendarClose={onCalendarClose}
              onChange={onDateChange}
              onCalendarOpen={() => handleReportTypeOnChange(null)}
            />
          </DatePickerWrapper>
          <StyledError>
            {errors?.dateRange && <span>{errors?.dateRange}</span>}
          </StyledError>
        </div>
      )}

      <ActionButtonsContainer>
        <AnimatedButton
          data-testid="generate-report"
          onClick={generateNutricionReport}
          icon={null}
          {...btnGenerateReportErrorMngmt}
        >
          Generate Report
        </AnimatedButton>
      </ActionButtonsContainer>
    </div>
  );
};

export default withTheme(ReportGeneratorOptions);
